import { ChakraProvider } from "@chakra-ui/react";
import "@splidejs/react-splide/css/core";
import {
  HydrationBoundary,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import NextApp, { AppContext, AppInitialProps } from "next/app";
import { Lexend } from "next/font/google";
import Script from "next/script";
import { useState } from "react";
import "swiper/css";
import "swiper/css/free-mode";
import { Layout } from "~/components/Layout/Layout";
import { Seo } from "~/components/Layout/Seo";
import {
  ConsentProvider,
  CookieBanner,
  MapLoaderProvider,
  NewsletterProvider,
  PageContextProvider,
} from "~/providers";
import {
  CustomAppProps,
  PageProps,
  SeoPageData,
} from "~/types";
import "~/styles/globals.css";
import { theme } from "~/theme";

const lexend = Lexend({ subsets: ["latin"] });

type InitialProps = {
  seoData: SeoPageData;
};
type AppProps = PageProps<InitialProps>;

const App = ({
  Component,
  pageProps: { dehydratedState, seoData, tourismoPageData, ...pageProps },
}: CustomAppProps<AppProps>) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000,
          },
        },
      }),
  );

  return (
    <>
      <style jsx global>
        {`
          :root {
            --font-lexend: ${lexend.style.fontFamily};
          }
        `}
      </style>
      <Script id="google-tag-manager" strategy="afterInteractive">
        {`
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','GTM-M8F6T9R6');
        `}
      </Script>
      <QueryClientProvider client={queryClient}>
        <HydrationBoundary
          state={dehydratedState ?? { queries: [], mutations: [] }}
        >
          <ChakraProvider theme={theme}>
            <PageContextProvider value={tourismoPageData}>
              <Seo seoData={seoData} />
              <MapLoaderProvider>
                <NewsletterProvider>
                  <ConsentProvider>
                    <Layout layoutOptions={Component.layoutOptions}>
                      <Component
                        {...pageProps}
                        tourismoPageData={tourismoPageData}
                      />
                      <CookieBanner />
                    </Layout>
                  </ConsentProvider>
                </NewsletterProvider>
              </MapLoaderProvider>
            </PageContextProvider>
          </ChakraProvider>
        </HydrationBoundary>
      </QueryClientProvider>
    </>
  );
};

App.getInitialProps = async (
  context: AppContext,
): Promise<AppInitialProps<InitialProps>> => {
  const appProps = await NextApp.getInitialProps(context);
  return { ...appProps, pageProps: { ...appProps.pageProps } };
};

export default App;
